import React from 'react'

import { ObjectHash } from '../../types/shared'
import { useForceUpdate } from './force-update'

type SubscriberFunc<T> = (value: T) => void

export class GlobalState<StateType extends ObjectHash = ObjectHash> {
  value: StateType
  subscribers: Array<SubscriberFunc<StateType>> = []

  constructor(initialValue: StateType = JSON.parse('{}')) {
    this.value = initialValue
  }

  getValue = () => this.value

  setValue = (newState: Partial<StateType>, shouldReplace = false) => {
    this.value = shouldReplace ? (newState as StateType) : { ...this.value, ...newState }

    this.subscribers.forEach((subscriber) => {
      subscriber(this.value)
    })
  }

  subscribe = (itemToSubscribe: SubscriberFunc<StateType>) => {
    if (this.subscribers.indexOf(itemToSubscribe) > -1) {
      // Already subsribed
      return
    }
    this.subscribers.push(itemToSubscribe)
  }

  unsubscribe = (itemToUnsubscribe: SubscriberFunc<StateType>) => {
    this.subscribers = this.subscribers.filter((subscriber) => subscriber !== itemToUnsubscribe)
  }
}

export function useGlobalState<T extends ObjectHash<any, string>>(
  globalState: GlobalState<T>
): [stateGetter: () => T, setState: SubscriberFunc<Partial<T>>] {
  const { forceUpdate } = useForceUpdate()

  React.useEffect(() => {
    globalState.subscribe(forceUpdate)
    return function () {
      globalState.unsubscribe(forceUpdate)
    }
  }, [forceUpdate, globalState])

  const setState = React.useCallback(
    (value: Partial<T>, shouldReplace = false) => {
      globalState.setValue(value, shouldReplace)
    },
    [globalState]
  )

  return [globalState.getValue, setState]
}
